import { formatCurrencyWithOptions as formatCurrencyWithOptionsBase } from "@hopper-i18n/formatter-base";

/**
 * Custom currency formatter for RBC that handles French Canadian (fr-CA) locale.
 *
 * For fr-CA locale with CAD currency:
 * - Standard format: "CA$300"
 * - French Canadian format: "300 $ CA"
 *
 * This formatter uses Intl.NumberFormat.formatToParts to rearrange the currency
 * symbol and code to match French Canadian conventions.
 */

// Cache for formatter instances to avoid recreating them on every call
const formatterCache = new Map<string, Intl.NumberFormat>();

// Serialize options to create a stable cache key
function serializeOptions(options?: Intl.NumberFormatOptions): string {
  if (!options) return "";
  return JSON.stringify(
    Object.keys(options)
      .sort()
      .reduce((acc, key) => ({ ...acc, [key]: options[key] }), {})
  );
}

// Get or create a cached formatter
function getFormatter(
  locale: string,
  currencyCode: string,
  options?: Intl.NumberFormatOptions
): Intl.NumberFormat {
  const cacheKey = `${locale}::${currencyCode}::${serializeOptions(options)}`;
  let formatter = formatterCache.get(cacheKey);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "code", // Use "CAD" instead of "$"
      ...options,
    });
    formatterCache.set(cacheKey, formatter);
  }

  return formatter;
}

export const formatCurrencyWithOptions = (params: {
  currencyCode: string;
  locale: string;
  amount: number;
  symbol?: string;
  options?: Intl.NumberFormatOptions;
}): string => {
  // Only apply custom formatting for French Canadian locale with CAD currency
  if (!(params.locale.startsWith("fr") && params.currencyCode === "CAD")) {
    return formatCurrencyWithOptionsBase(params);
  }

  // Get or create cached formatter
  const formatter = getFormatter(
    params.locale,
    params.currencyCode,
    params.options
  );

  // Format the number and get individual parts
  const parts = formatter.formatToParts(params.amount);

  // Reconstruct in French Canadian format: "300 $ CA"
  return parts
    .map((part) => {
      if (part.type === "currency") {
        // Replace "CAD" with "$ CA" (using non-breaking space)
        return "$\u00A0CA";
      } else {
        // Return all other parts as-is (preserves non-breaking spaces for thousands separators)
        return part.value;
      }
    })
    .join("");
};
